import { AxiosStatic } from 'axios';

import { PollingPhoneCallGatewayWithHangupFunctionalityStrategy } from 'components/phoneCall/phoneCallGateway/PollingPhoneCallGatewayWithHangupFunctionalityStrategy';
import { usePhoneCallStore } from 'src/stores/phoneCall';

import { AbstractPollingPhoneCallGatewayWithHangupFunctionalityStrategy } from 'src/components/phoneCall/phoneCallGateway/AbstractPollingPhoneCallGatewayWithHangupFunctionalityStrategy';
import { phoneCallProviderRequestConfig } from 'src/components/phoneCall/phoneCallGateway/phoneCallProviderRequestConfig';
import {
	ProviderRequestTarget,
	providerRequestTarget,
} from 'src/components/phoneCall/phoneCallGateway/providerRequestTarget';
import {
	StarfaceAuthToken,
	StarfaceXmlRpcResponse,
} from 'src/components/phoneCall/phoneCallGateway/starface/InterfacesAndTypes';

import { OutgoingCallConfigurationForTheUser } from 'src/models/phoneCall';

import { CommandBus } from 'src/util/command/CommandBus';
import { hashSha512 } from 'src/util/common';
import { QueryStore } from 'src/util/query/QueryStore';

type PhoneCallStore = ReturnType<typeof usePhoneCallStore>;

export class StarfacePollingPhoneCallGatewayWithHangupFunctionalityStrategy
	extends AbstractPollingPhoneCallGatewayWithHangupFunctionalityStrategy
	implements PollingPhoneCallGatewayWithHangupFunctionalityStrategy
{
	private readonly axios: AxiosStatic;
	private outGoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser | null = null;

	constructor(
		axios: AxiosStatic,
		phoneCallStore: PhoneCallStore,
		queryStore: QueryStore,
		commandBus: CommandBus,
		authenticatedUserId: number,
		outGoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser
	) {
		super(phoneCallStore, queryStore, commandBus, authenticatedUserId);
		this.axios = axios;
		this.outGoingCallConfigurationForTheUser = outGoingCallConfigurationForTheUser;
	}

	private xmlRpcTarget(authToken: StarfaceAuthToken): ProviderRequestTarget {
		const token = `${authToken.loginId}:${authToken.hashedToken}`;
		const domain = this.outGoingCallConfigurationForTheUser?.domain as string;

		return providerRequestTarget(`https://${domain}/xml-rpc?de.vertico.starface.auth=${encodeURIComponent(token)}`);
	}

	public async makeCall(callerName: string, number: string): Promise<null | string> {
		if (!this.isConfigurationValid()) {
			return null;
		}

		const timestamp = String(Math.floor(Date.now() / 1000));
		const authToken = await this.generateAuthToken();
		const method = `${this.outGoingCallConfigurationForTheUser?.moduleName as string}.PlaceCallWithNumber`;

		const xmlRequest = this.buildXmlRpcRequest(method, {
			DestinationNumber: number,
			STARFACE_LOGIN_ID: this.outGoingCallConfigurationForTheUser?.terminal as string,
		});

		try {
			const target = this.xmlRpcTarget(authToken);
			const response = await this.axios.post(
				target.url,
				xmlRequest,
				phoneCallProviderRequestConfig({
					headers: {
						'Content-Type': 'text/xml',
						...target.headers,
					},
					withCredentials: false,
				})
			);

			const xmlRpcResponse = this.parseXmlRpcResponse(response.data as string);

			if (xmlRpcResponse.success && xmlRpcResponse.callId) {
				this.setActiveCallId(xmlRpcResponse.callId);
				return await this.createPhoneCall(callerName, number);
			}
		} catch {
			/* empty */
		}

		const callId = await this.nextOutgoingCallIdForACalledNumberAfterAGivenTimestamp(number, timestamp);

		this.setActiveCallId(callId as string);

		return callId;
	}

	public endCall(): void {
		if (!this.isConfigurationValid() || this.activeCallId() === null) {
			this.clearActiveCall();
			return;
		}

		void (async () => {
			const authToken = await this.generateAuthToken();
			const method = `${this.outGoingCallConfigurationForTheUser?.moduleName as string}.HangUpCall`;

			const xmlRequest = this.buildXmlRpcRequest(method, {
				STARFACE_LOGIN_ID: this.outGoingCallConfigurationForTheUser?.terminal as string,
			});

			try {
				const target = this.xmlRpcTarget(authToken);
				await this.axios.post(
					target.url,
					xmlRequest,
					phoneCallProviderRequestConfig({
						headers: {
							'Content-Type': 'text/xml',
							...target.headers,
						},
						withCredentials: false,
					})
				);
			} catch {
				/* empty */
			}
		})();

		this.clearActiveCall();
	}

	private isConfigurationValid(): boolean {
		return !!(
			this.outGoingCallConfigurationForTheUser?.domain &&
			this.outGoingCallConfigurationForTheUser?.adminUser &&
			this.outGoingCallConfigurationForTheUser?.adminPassword &&
			this.outGoingCallConfigurationForTheUser?.moduleName &&
			this.outGoingCallConfigurationForTheUser?.terminal
		);
	}

	private async generateAuthToken(): Promise<StarfaceAuthToken> {
		const loginId = this.outGoingCallConfigurationForTheUser?.adminUser as string;
		const password = this.outGoingCallConfigurationForTheUser?.adminPassword as string;

		const hash1 = await hashSha512(password);
		const concat = `${loginId}*${hash1}`;
		const hash2 = await hashSha512(concat);

		return {
			hashedToken: hash2,
			loginId: loginId,
		};
	}

	private buildXmlRpcRequest(method: string, params: Record<string, string>): string {
		let xmlParams = '';
		for (const [key, value] of Object.entries(params)) {
			xmlParams += `
				<member>
					<name>${key}</name>
					<value><string>${value}</string></value>
				</member>`;
		}

		return `<?xml version="1.0"?>
<methodCall>
	<methodName>${method}</methodName>
	<params>
		<param>
			<value>
				<struct>${xmlParams}
				</struct>
			</value>
		</param>
	</params>
</methodCall>`;
	}

	private parseXmlRpcResponse(xmlResponse: string): StarfaceXmlRpcResponse {
		try {
			const callMatch = xmlResponse.match(
				/<name>call<\/name>\s*<\/member>\s*<member>\s*<name>[^<]*<\/name>\s*<value>\s*<string>([^<]+)<\/string>/
			);

			if (callMatch && callMatch[1]) {
				return {
					callId: callMatch[1],
					success: true,
				};
			}

			const hasMethodResponse = xmlResponse.includes('<methodResponse>');
			const hasFault = xmlResponse.includes('<fault>');

			return {
				success: hasMethodResponse && !hasFault,
			};
		} catch {
			return {
				success: false,
			};
		}
	}
}
